import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { useStore } from "@/state/store";

/**
 * Reviewer-Magic-Link landing page.
 *
 * Reads `?token=<jwt>` from the URL, exchanges it via `GET /auth/magic`,
 * persists the resulting session in the store (as a read-only reviewer)
 * and redirects to `/present` so the reviewer immediately sees the
 * presenter view in read-only mode.
 *
 * Failures (invalid / expired token) are surfaced inline with a "Zur
 * Anmeldung" fallback that just routes to `/`.
 */
export function MagicLinkPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { dispatch } = useStore();
  const [error, setError] = useState<string | null>(null);
  const tried = useRef(false);

  useEffect(() => {
    if (tried.current) return;
    tried.current = true;
    const token = params.get("token");
    if (!token) {
      setError("Kein Token in der URL.");
      return;
    }
    (async () => {
      try {
        const res = await api.auth.magic(token);
        dispatch({
          type: "auth/login",
          token: res.token,
          role: res.role,
          readOnly: true,
        });
        // Strip the token from the URL so it doesn't sit in browser history.
        try {
          window.history.replaceState(null, "", "/");
        } catch {
          /* noop */
        }
        navigate("/present", { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 401)
            setError("Token ungültig — bitte einen neuen Link anfordern.");
          else if (err.status === 410)
            setError("Link abgelaufen — bitte einen neuen Link anfordern.");
          else if (err.status === 403)
            setError("Token-Typ ungültig.");
          else setError("Magic-Link konnte nicht eingelöst werden.");
        } else {
          setError("Magic-Link konnte nicht eingelöst werden.");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="login-hero">
      <div className="login-hero-bg" aria-hidden="true" />
      <div className="login-card" style={{ textAlign: "center" }}>
        <header style={{ display: "grid", gap: 4 }}>
          <div className="deck-eyebrow">SVO Offsite · Reviewer-Zugang</div>
          <h1
            className="deck-title"
            style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", margin: 0 }}
          >
            {error ? "Link ungültig" : "Einen Moment …"}
          </h1>
        </header>
        {error ? (
          <>
            <p className="form-error">{error}</p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => navigate("/", { replace: true })}
            >
              Zur Anmeldung
            </button>
          </>
        ) : (
          <p className="form-hint">Token wird geprüft.</p>
        )}
      </div>
    </main>
  );
}
